import { View } from "@react-pdf/renderer";
import { useContext } from "react";
import { perelynRed } from "./const";
import { CVContentContext, LanguageContext } from "./CVPDF";
import { HeaderRow, Row, Table } from "./TableComponents";
import { SmallTextPDF } from "./Texts";
import { cvTranslations } from "./translations";

function RatingSquares({ value, max }: { value: number; max: number }) {
  return (
    <View style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
      {Array.from({ length: max }).map((_, index) => (
        <View
          key={index}
          style={{
            width: "10px",
            height: "10px",
            backgroundColor: index < value ? perelynRed : "#E8E8E8",
            borderRadius: "2px",
          }}
        />
      ))}
    </View>
  );
}

function RatingProgressBar({ value, max }: { value: number; max: number }) {
  const fillPercent = max > 0 ? (value / max) * 100 : 0;
  return (
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "6px",
      }}
    >
      <View
        style={{
          width: "70px",
          height: "10px",
          backgroundColor: "#E8E8E8",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <View
          style={{
            width: `${fillPercent}%`,
            height: "100%",
            backgroundColor: perelynRed,
            borderRadius: "2px",
          }}
        />
      </View>
      <SmallTextPDF style={{ fontSize: "8px", color: "#555" }}>
        {value}/{max}
      </SmallTextPDF>
    </View>
  );
}

function RatingBar({ value, maxRating }: { value: number; maxRating: number }) {
  if (maxRating <= 5) {
    return <RatingSquares value={value} max={maxRating} />;
  }
  return <RatingProgressBar value={value} max={maxRating} />;
}

function formatExperience(
  years: number,
  months: number,
  language: "en" | "de",
) {
  const t = cvTranslations[language];
  const parts: string[] = [];

  if (years > 0) {
    parts.push(`${years} ${years === 1 ? t.year : t.years}`);
  }
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? t.month : t.months}`);
  }

  return parts.join(", ");
}

interface RequirementData {
  requirement: string;
  rating: number;
  experienceYears: number;
  experienceMonths: number;
}

export default function RequirementsMatchTable() {
  const cvContent = useContext<any>(CVContentContext) || {};
  const language = useContext(LanguageContext);
  const t = cvTranslations[language];

  // Get requirements match data from cvContent
  const requirementsMatch = cvContent.requirementsMatch;

  // Only render if requirements match data is present
  if (
    !requirementsMatch ||
    !requirementsMatch.requirements ||
    requirementsMatch.requirements.length === 0
  ) {
    return null;
  }

  const jobTitle =
    requirementsMatch.positionName || requirementsMatch.roleTitle || "";
  const requirements: RequirementData[] = requirementsMatch.requirements;

  // Infer max rating from the data: if any rating > 5, assume 1-10 scale
  const maxRating = requirements.some((r) => r.rating > 5) ? 10 : 5;

  return (
    <Table>
      <HeaderRow
        label={
          jobTitle
            ? `${t.requirementsMatch}${jobTitle} ${t.role.toLowerCase()}`
            : t.requirementsMatchOnly
        }
      />
      {requirements.map((item, index) => (
        <Row key={index} last={index === requirements.length - 1}>
          <SmallTextPDF style={{ width: "40%", fontSize: "9px" }}>
            {item.requirement.charAt(0).toUpperCase() +
              item.requirement.slice(1)}
          </SmallTextPDF>
          <View style={{ width: "30%", paddingTop: "2px" }}>
            <RatingBar value={item.rating} maxRating={maxRating} />
          </View>
          <SmallTextPDF style={{ width: "30%", fontSize: "9px" }}>
            {item.experienceYears > 0 || item.experienceMonths > 0
              ? formatExperience(
                  item.experienceYears,
                  item.experienceMonths,
                  language,
                )
              : "-"}
          </SmallTextPDF>
        </Row>
      ))}
    </Table>
  );
}
